// @ts-nocheck
import {useState, useEffect, useRef} from 'react'
import {useParams, useNavigate} from 'react-router-dom'
import axios from 'axios'
import './customer-portal.scss'

const API_URL = process.env.REACT_APP_API_URL

type View = 'list' | 'detail' | 'new'

interface TicketSummary {
  _id: string
  ticket_number: string
  subject: string
  status: string
  priority: string
  customer_unread: boolean
  created_at: string
  updated_at: string
}

interface Message {
  _id: string
  sender_type: 'customer' | 'admin' | 'system'
  message: string
  created_at: string
  attachments: any[]
}

interface TicketDetail {
  _id: string
  ticket_number: string
  subject: string
  status: string
  priority: string
  created_at: string
  updated_at: string
}

const STATUS_MAP: Record<string, {label: string; color: string}> = {
  open: {label: 'פתוח', color: '#007aff'},
  in_progress: {label: 'בטיפול', color: '#ff9500'},
  in_development: {label: 'בפיתוח', color: '#af52de'},
  waiting_on_customer: {label: 'ממתין לתגובה', color: '#ff3b30'},
  follow_up: {label: 'מעקב', color: '#5856d6'},
  resolved: {label: 'נפתר', color: '#34c759'},
  closed: {label: 'סגור', color: '#8e8e93'},
}

function formatDate(dateStr: string) {
  const d = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'עכשיו'
  if (diffMins < 60) return `לפני ${diffMins} דקות`
  if (diffHours < 24) return `לפני ${diffHours} שעות`
  if (diffDays < 7) return `לפני ${diffDays} ימים`
  return d.toLocaleDateString('he-IL')
}

function formatTime(dateStr: string) {
  const d = new Date(dateStr)
  return d.toLocaleTimeString('he-IL', {hour: '2-digit', minute: '2-digit'})
}

function formatFullDate(dateStr: string) {
  const d = new Date(dateStr)
  return d.toLocaleDateString('he-IL', {day: 'numeric', month: 'long', year: 'numeric'})
}

export function CustomerPortal() {
  const {token, ticketId: urlTicketId} = useParams<{token: string; ticketId?: string}>()
  const navigate = useNavigate()
  const [view, setView] = useState<View>('list')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [customerName, setCustomerName] = useState('')
  const [tickets, setTickets] = useState<TicketSummary[]>([])

  // Detail view state
  const [selectedTicket, setSelectedTicket] = useState<TicketDetail | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [detailLoading, setDetailLoading] = useState(false)
  const [replyText, setReplyText] = useState('')
  const [sending, setSending] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // New ticket state
  const [newSubject, setNewSubject] = useState('')
  const [newMessage, setNewMessage] = useState('')
  const [newFiles, setNewFiles] = useState<File[]>([])
  const [creating, setCreating] = useState(false)
  const newFileRef = useRef<HTMLInputElement>(null)

  // Reply file state
  const [replyFiles, setReplyFiles] = useState<File[]>([])
  const replyFileRef = useRef<HTMLInputElement>(null)

  // Hide splash screen
  useEffect(() => {
    const splashScreen = document.getElementById('splash-screen')
    if (splashScreen) {
      splashScreen.style.display = 'none'
    }
  }, [])

  // Load tickets
  useEffect(() => {
    if (token) {
      loadTickets()
    } else {
      setError('קישור לא תקין')
      setLoading(false)
    }
  }, [token])

  // Auto-open ticket from URL
  useEffect(() => {
    if (urlTicketId && !loading && tickets.length > 0 && !selectedTicket) {
      openTicket(urlTicketId)
    }
  }, [urlTicketId, loading, tickets.length])

  // Poll for new messages every 10 seconds when viewing a ticket
  useEffect(() => {
    if (view !== 'detail' || !selectedTicket || !token) return
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API_URL}/api/customer-portal/${token}/tickets/${selectedTicket._id}`)
        if (res.data.messages.length !== messages.length) {
          setMessages(res.data.messages)
        }
      } catch (e) {
        // ignore polling errors
      }
    }, 10000)
    return () => clearInterval(interval)
  }, [view, selectedTicket?._id, token, messages.length])

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({behavior: 'smooth'})
    }
  }, [messages])

  const loadTickets = async () => {
    try {
      setLoading(true)
      const res = await axios.get(`${API_URL}/api/customer-portal/${token}`)
      setCustomerName(res.data.customer_name)
      setTickets(res.data.tickets)
      setError('')
    } catch (err: any) {
      setError(err.response?.data?.error || 'שגיאה בטעינת הנתונים')
    } finally {
      setLoading(false)
    }
  }

  const openTicket = async (ticketId: string) => {
    try {
      setDetailLoading(true)
      setView('detail')
      navigate(`/my-tickets/${token}/${ticketId}`, { replace: true })
      const res = await axios.get(`${API_URL}/api/customer-portal/${token}/tickets/${ticketId}`)
      setSelectedTicket(res.data.ticket)
      setMessages(res.data.messages)
    } catch (err: any) {
      setError(err.response?.data?.error || 'שגיאה בטעינת הפנייה')
      setView('list')
    } finally {
      setDetailLoading(false)
    }
  }

  const handleReply = async (e: React.FormEvent) => {
    e.preventDefault()
    if ((!replyText.trim() && replyFiles.length === 0) || !selectedTicket) return

    setSending(true)
    try {
      const formData = new FormData()
      formData.append('message', replyText.trim())
      replyFiles.forEach((f) => formData.append('attachments', f))

      const res = await axios.post(
        `${API_URL}/api/customer-portal/${token}/tickets/${selectedTicket._id}/reply`,
        formData,
        {headers: {'Content-Type': 'multipart/form-data'}}
      )
      setMessages((prev) => [...prev, res.data.message])
      setReplyText('')
      setReplyFiles([])
    } catch (err: any) {
      alert(err.response?.data?.error || 'שגיאה בשליחת ההודעה')
    } finally {
      setSending(false)
    }
  }

  const handleCreateTicket = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newSubject.trim() || !newMessage.trim()) return

    setCreating(true)
    try {
      const formData = new FormData()
      formData.append('subject', newSubject.trim())
      formData.append('message', newMessage.trim())
      newFiles.forEach((f) => formData.append('attachments', f))

      const res = await axios.post(
        `${API_URL}/api/customer-portal/${token}/tickets`,
        formData,
        {headers: {'Content-Type': 'multipart/form-data'}}
      )
      setNewSubject('')
      setNewMessage('')
      setNewFiles([])
      openTicket(res.data.ticket._id)
      loadTickets()
    } catch (err: any) {
      alert(err.response?.data?.error || 'שגיאה ביצירת הפנייה')
    } finally {
      setCreating(false)
    }
  }

  const goToList = () => {
    setView('list')
    setSelectedTicket(null)
    setMessages([])
    setReplyText('')
    navigate(`/my-tickets/${token}`, { replace: true })
    loadTickets()
  }

  const getDateLabel = (dateStr: string) => {
    const d = new Date(dateStr)
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)

    if (d.toDateString() === today.toDateString()) return 'היום'
    if (d.toDateString() === yesterday.toDateString()) return 'אתמול'
    return formatFullDate(dateStr)
  }

  // Loading state
  if (loading) {
    return (
      <div className='cp' dir='rtl'>
        <div className='cp-shell'>
          <div className='cp-loading'>
            <div className='cp-spinner'></div>
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (error && !tickets.length && view === 'list') {
    return (
      <div className='cp' dir='rtl'>
        <div className='cp-shell'>
          <div className='cp-nav'>
            <img src='https://fbzipper.com/assets/d-code-logo-dark-1d926488.svg' alt='FBZipper' className='cp-logo' />
          </div>
          <div className='cp-error-state'>
            <div className='cp-error-icon'>!</div>
            <h2>לא ניתן לטעון את הדף</h2>
            <p>{error}</p>
          </div>
        </div>
      </div>
    )
  }

  // Detail view
  if (view === 'detail') {
    return (
      <div className='cp' dir='rtl'>
        <div className='cp-shell'>
          <div className='cp-nav'>
            <button className='cp-back' onClick={goToList}>
              <i className='bi bi-chevron-right'></i>
              <span>חזרה</span>
            </button>
          </div>

          {selectedTicket && (
            <div className='cp-detail-header'>
              <div className='cp-detail-title'>
                <h1>{selectedTicket.subject}</h1>
                <div className='cp-detail-meta'>
                  <span
                    className='cp-status'
                    style={{backgroundColor: STATUS_MAP[selectedTicket.status]?.color || '#8e8e93'}}
                  >
                    {STATUS_MAP[selectedTicket.status]?.label || selectedTicket.status}
                  </span>
                  <span className='cp-ticket-id'>{selectedTicket.ticket_number}</span>
                </div>
              </div>
            </div>
          )}

          <div className='cp-messages'>
            {detailLoading ? (
              <div className='cp-loading'>
                <div className='cp-spinner'></div>
              </div>
            ) : (
              <>
                {messages.map((msg, idx) => {
                  const isCustomer = msg.sender_type === 'customer'
                  const showDate =
                    idx === 0 ||
                    getDateLabel(msg.created_at) !== getDateLabel(messages[idx - 1].created_at)

                  return (
                    <div key={msg._id}>
                      {showDate && (
                        <div className='cp-date-sep'>
                          <span>{getDateLabel(msg.created_at)}</span>
                        </div>
                      )}
                      <div className={`cp-bubble ${isCustomer ? 'is-customer' : 'is-support'}`}>
                        {!isCustomer && <div className='cp-bubble-sender'>צוות תמיכה</div>}
                        <div className='cp-bubble-text'>{msg.message}</div>
                        {msg.attachments && msg.attachments.length > 0 && (
                          <div className='cp-bubble-files'>
                            {msg.attachments.map((att: any, i: number) => (
                              <a key={i} href={att.location} target='_blank' rel='noopener noreferrer'>
                                <i className='bi bi-paperclip'></i>
                                {att.originalname}
                              </a>
                            ))}
                          </div>
                        )}
                        <div className='cp-bubble-time'>{formatTime(msg.created_at)}</div>
                      </div>
                    </div>
                  )
                })}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          <form className='cp-reply' onSubmit={handleReply}>
            <input
              ref={replyFileRef}
              type='file'
              multiple
              accept='image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip'
              onChange={(e) => {
                const files = Array.from(e.target.files || [])
                setReplyFiles((prev) => [...prev, ...files].slice(0, 5))
                if (replyFileRef.current) replyFileRef.current.value = ''
              }}
              style={{display: 'none'}}
            />
            {replyFiles.length > 0 && (
              <div className='cp-reply-files'>
                {replyFiles.map((f, i) => (
                  <div key={i} className='cp-reply-file-chip'>
                    <span>{f.name}</span>
                    <button
                      type='button'
                      onClick={() => setReplyFiles((prev) => prev.filter((_, idx) => idx !== i))}
                    >
                      <i className='bi bi-x'></i>
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className='cp-reply-row'>
              <button
                type='button'
                className='cp-attach-icon'
                onClick={() => replyFileRef.current?.click()}
                disabled={sending}
              >
                <i className='bi bi-paperclip'></i>
              </button>
              <input
                type='text'
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder='כתוב תגובה...'
                disabled={sending}
              />
              <button type='submit' disabled={sending || (!replyText.trim() && replyFiles.length === 0)}>
                {sending ? <div className='cp-spinner sm'></div> : <i className='bi bi-arrow-up'></i>}
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  }

  // New ticket view
  if (view === 'new') {
    return (
      <div className='cp' dir='rtl'>
        <div className='cp-shell'>
          <div className='cp-nav'>
            <button className='cp-back' onClick={goToList}>
              <i className='bi bi-chevron-right'></i>
              <span>חזרה</span>
            </button>
          </div>

          <div className='cp-form-wrapper'>
            <h1>פנייה חדשה</h1>
            <p className='cp-form-desc'>נשמח לעזור. מלא את הפרטים ונחזור אליך בהקדם.</p>

            <form className='cp-form' onSubmit={handleCreateTicket}>
              <div className='cp-field'>
                <label>נושא</label>
                <input
                  type='text'
                  value={newSubject}
                  onChange={(e) => setNewSubject(e.target.value)}
                  placeholder='תיאור קצר של הפנייה'
                  maxLength={255}
                  required
                />
              </div>
              <div className='cp-field'>
                <label>הודעה</label>
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder='פרט את הבעיה או הבקשה...'
                  rows={5}
                  required
                />
              </div>
              <div className='cp-field'>
                <label>קבצים מצורפים</label>
                <input
                  ref={newFileRef}
                  type='file'
                  multiple
                  accept='image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip'
                  onChange={(e) => {
                    const files = Array.from(e.target.files || [])
                    setNewFiles((prev) => [...prev, ...files].slice(0, 5))
                  }}
                  style={{display: 'none'}}
                />
                <button
                  type='button'
                  className='cp-attach-btn'
                  onClick={() => newFileRef.current?.click()}
                >
                  <i className='bi bi-paperclip'></i>
                  הוסף קבצים (עד 5)
                </button>
                {newFiles.length > 0 && (
                  <div className='cp-file-list'>
                    {newFiles.map((f, i) => (
                      <div key={i} className='cp-file-item'>
                        <i className='bi bi-file-earmark'></i>
                        <span>{f.name}</span>
                        <button
                          type='button'
                          onClick={() => setNewFiles((prev) => prev.filter((_, idx) => idx !== i))}
                        >
                          <i className='bi bi-x'></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button
                type='submit'
                className='cp-submit'
                disabled={creating || !newSubject.trim() || !newMessage.trim()}
              >
                {creating ? <div className='cp-spinner sm'></div> : 'שליחה'}
              </button>
            </form>
          </div>
        </div>
      </div>
    )
  }

  // List view
  return (
    <div className='cp' dir='rtl'>
      <div className='cp-shell'>
        {/* Top nav */}
        <div className='cp-nav'>
          <img src='https://fbzipper.com/assets/d-code-logo-dark-1d926488.svg' alt='FBZipper' className='cp-logo' />
          <a href='/auth' className='cp-dashboard-link'>
            כניסה למערכת
            <i className='bi bi-box-arrow-in-left'></i>
          </a>
        </div>

        {/* Hero */}
        <div className='cp-hero'>
          <h1>{customerName ? `היי ${customerName},` : 'התמיכה שלך'}</h1>
          <p>כאן תוכל לעקוב אחרי הפניות שלך ולפתוח פנייה חדשה.</p>
        </div>

        {/* Actions */}
        <div className='cp-actions'>
          <button className='cp-new-btn' onClick={() => setView('new')}>
            <div className='cp-new-btn-icon'>
              <i className='bi bi-plus'></i>
            </div>
            <div className='cp-new-btn-text'>
              <span>פנייה חדשה</span>
              <small>פתח פנייה לצוות התמיכה</small>
            </div>
            <i className='bi bi-chevron-left cp-new-btn-arrow'></i>
          </button>
        </div>

        {/* Tickets */}
        <div className='cp-section'>
          <h2>הפניות שלך</h2>
          {tickets.length === 0 ? (
            <div className='cp-empty'>
              <p>אין פניות עדיין</p>
            </div>
          ) : (
            <div className='cp-ticket-list'>
              {tickets.map((ticket) => (
                <div
                  key={ticket._id}
                  className={`cp-ticket ${ticket.customer_unread ? 'has-unread' : ''}`}
                  onClick={() => openTicket(ticket._id)}
                >
                  <div className='cp-ticket-right'>
                    <div className='cp-ticket-subject'>
                      {ticket.customer_unread && <span className='cp-dot'></span>}
                      {ticket.subject}
                    </div>
                    <div className='cp-ticket-info'>
                      <span
                        className='cp-status'
                        style={{backgroundColor: STATUS_MAP[ticket.status]?.color || '#8e8e93'}}
                      >
                        {STATUS_MAP[ticket.status]?.label || ticket.status}
                      </span>
                      <span className='cp-ticket-date'>{formatDate(ticket.updated_at)}</span>
                    </div>
                  </div>
                  <i className='bi bi-chevron-left cp-ticket-arrow'></i>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className='cp-footer'>
          <a href='/auth' className='cp-footer-link'>כניסה למערכת הניהול</a>
          <p>&copy; {new Date().getFullYear()} FBZipper. כל הזכויות שמורות.</p>
        </div>
      </div>
    </div>
  )
}
